import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';
import {
  MONTHS_FULL,
  buildCivilDate,
  civilDateParts,
  daysInMonth,
  formatCivilRange,
  type CivilDate,
  type DateRange,
} from './civil-date.js';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export interface DateRangePickerProps {
  label: string;
  value: DateRange;
  onValueChange: (value: DateRange) => void;
  /// Data civil usada como "hoje" — quem decide é o fuso do negócio, não o do navegador.
  today: CivilDate;
  /// O que o botão diz quando não há intervalo escolhido. É também o nome do que volta a valer ao
  /// limpar: numa tela que já trabalha por mês, limpar devolve o recorte do mês.
  emptyLabel?: string | undefined;
  /// `toolbar` é o controle de barra de filtros, do tamanho dos vizinhos. `field` é o campo de
  /// formulário, com rótulo à vista e a mesma moldura dos demais campos.
  variant?: 'toolbar' | 'field' | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/// Seletor de intervalo de datas civis: um clique marca o começo, o segundo marca o fim, e o mesmo
/// dia clicado duas vezes é um dia só. Nasceu porque a conciliação só sabia filtrar pelo mês de
/// trabalho inteiro: procurar o pagamento de uma terça-feira obrigava a ler o mês inteiro.
///
/// Não usa o seletor nativo do navegador — ele não faz intervalo, muda de aparência a cada
/// plataforma e ignora o tema do sistema.
export function DateRangePicker({
  label,
  value,
  onValueChange,
  today,
  emptyLabel = 'Qualquer dia',
  variant = 'toolbar',
  disabled,
  className,
}: DateRangePickerProps) {
  const field = variant === 'field';
  const id = useId();
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => civilDateParts(value.from ?? today));
  /// O dia sob o cursor enquanto só há começo escolhido: sem ele, quem arrasta o olhar pela grade
  /// não vê o intervalo que está prestes a criar.
  const [hovered, setHovered] = useState<CivilDate | null>(null);
  /// O começo escolhido **nesta abertura** do painel, que é o que decide se o próximo clique abre
  /// ou fecha o intervalo.
  ///
  /// Não dá para perguntar isso ao valor recebido: há tela que nunca devolve fim vazio — a agenda
  /// de um dia é um intervalo de um dia —, e ali o painel veria intervalo completo a cada clique,
  /// recomeçaria para sempre e nunca fecharia.
  const [pending, setPending] = useState<CivilDate | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setHovered(null);
    setPending(null);
  }, []);

  const total = daysInMonth(cursor.year, cursor.month);
  const firstWeekday = new Date(Date.UTC(cursor.year, cursor.month - 1, 1)).getUTCDay();
  const monthName = MONTHS_FULL[cursor.month - 1] ?? '';
  // Enquanto o fim não foi escolhido, o dia sob o cursor faz as vezes dele.
  const tentative = pending && hovered && hovered >= pending ? hovered : null;
  const start = pending ?? value.from;
  const end = pending ? tentative : value.to;

  function shiftMonth(step: number) {
    const months = cursor.year * 12 + (cursor.month - 1) + step;
    setCursor({ year: Math.floor(months / 12), month: (months % 12) + 1, day: cursor.day });
  }

  /// Primeiro clique abre o intervalo; o segundo o fecha. Clicar antes do começo não devolve um
  /// intervalo invertido: recomeça dali, que é o que a pessoa quis dizer.
  function pick(day: CivilDate) {
    if (pending === null || day < pending) {
      setPending(day);
      onValueChange({ from: day, to: null });
      setHovered(null);
      return;
    }
    onValueChange({ from: pending, to: day });
    close();
  }

  return (
    <div className={cn('relative min-w-0', field && 'space-y-2', className)}>
      <span
        id={`${id}-label`}
        className={field ? 'block text-xs font-semibold text-content/70' : 'sr-only'}
      >
        {label}
      </span>
      <button
        type="button"
        ref={trigger}
        disabled={disabled}
        aria-labelledby={`${id}-label ${id}-value`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) setCursor(civilDateParts(value.from ?? today));
          setOpen((current) => !current);
        }}
        className={cn(
          'flex cursor-pointer items-center gap-2 text-sm text-content transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-50',
          field
            ? 'h-12 w-full justify-between rounded-xl border border-surface-border bg-surface-elevated px-4 hover:border-brand-500'
            : 'h-11 w-full min-w-0 justify-between rounded-xl border border-surface-border bg-surface-card px-3 font-semibold hover:border-brand-500/50 lg:w-auto lg:min-w-[13.5rem]',
        )}
      >
        <span id={`${id}-value`} className="truncate">
          {formatCivilRange(value, emptyLabel)}
        </span>
        <CalendarRange className="size-4 shrink-0 text-brand-400" aria-hidden="true" />
      </button>

      {open ? (
        <FloatingPanel
          anchorRef={trigger}
          onDismiss={close}
          align="start"
          role="dialog"
          aria-label={`Escolher ${label.toLocaleLowerCase('pt-BR')}`}
          className="w-[19rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              aria-label="Mês anterior"
              className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronLeft className="size-4" />
            </button>
            <strong aria-live="polite" className="text-xs font-semibold">
              {monthName} de {cursor.year}
            </strong>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              aria-label="Próximo mês"
              className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          <p className="mb-2 text-center text-[11px] text-content-faint">
            {pending ? 'Agora escolha o último dia.' : 'Escolha o primeiro dia.'}
          </p>

          <div className="grid grid-cols-7 gap-1" role="group" aria-label={`Dias de ${label}`}>
            {WEEKDAYS.map((weekday, index) => (
              <span
                key={`${weekday}-${String(index)}`}
                aria-hidden="true"
                className="grid h-8 place-items-center text-[10px] font-semibold uppercase text-content-faint"
              >
                {weekday}
              </span>
            ))}
            {Array.from({ length: firstWeekday }, (_item, index) => (
              <span key={`empty-${String(index)}`} />
            ))}
            {Array.from({ length: total }, (_item, index) => index + 1).map((day) => {
              const candidate = buildCivilDate(cursor.year, cursor.month, day);
              const edge = candidate === start || candidate === end;
              const inside = Boolean(start && end && candidate > start && candidate < end);
              const isToday = candidate === today;
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={edge || inside}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={`${String(day)} de ${monthName.toLocaleLowerCase('pt-BR')} de ${String(cursor.year)}`}
                  onClick={() => pick(candidate)}
                  onMouseEnter={() => setHovered(candidate)}
                  onFocus={() => setHovered(candidate)}
                  className={cn(
                    'grid h-9 cursor-pointer place-items-center rounded-lg text-xs tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
                    edge && 'bg-brand-500 font-semibold text-on-brand',
                    inside && 'bg-brand-500/15 text-content',
                    !edge &&
                      !inside &&
                      'text-content-secondary hover:bg-surface-elevated hover:text-content',
                    !edge && isToday && 'border border-brand-500/40 font-semibold',
                  )}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex justify-between gap-2 border-t border-surface-border pt-2">
            <button
              type="button"
              onClick={() => {
                onValueChange({ from: null, to: null });
                close();
              }}
              className="min-h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              {emptyLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                onValueChange({ from: today, to: today });
                close();
              }}
              className="min-h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold text-brand-400 transition hover:bg-brand-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              Hoje
            </button>
          </div>
        </FloatingPanel>
      ) : null}
    </div>
  );
}
